import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Container, useApp } from "@pixi/react";
import { Settings } from "../../../settings";
import Reel from "../Reel/Reel";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import ReelSetMask from "../ReelSetMask/ReelSetMask";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import SpineAnimationData from "../../../utils/SpineAnimationData";
import FontGenerator from "../../../utils/FontGenerator";
import { useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectHoldAndWinActive,
  selectFreeSpinActive,
  selectAutoPlayActive,
} from "../../../State/selectors";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";
import { useReelControl } from "../../../Contexts/ReelControlContext";

// Module-level cache for current round reels - set once, never changes
// This prevents Redux re-renders from affecting ReelSet
let cachedReels = null;

const ReelSet = memo(
  ({ symbolWidth, symbolHeight, dividerDimensions, reelSpacing }) => {
    const app = useApp();
    const { reelRefs, reelSetMaskRef } = useReelRefs();
    const { triggerReelLandingAnimations } = useReelLandingAnimations();
    const { spacebarTurboActive } = useReelControl();
    const currentRound = useSelector(selectCurrentRound);
    const autoPlayActive = useSelector(selectAutoPlayActive);
    const maskRef = useRef(null);
    const [maskReady, setMaskReady] = useState(false);
    const holdAndWinActive = useSelector(selectHoldAndWinActive);
    const freeSpinActive = useSelector(selectFreeSpinActive);

    // Cache reels at module level - set once and freeze the reference
    // This prevents re-renders when Redux state updates
    if (!cachedReels && currentRound?.reels) {
      cachedReels = currentRound.reels;
    }

    // Use cached reels, sliced to configured REEL_COUNT (server may send more)
    const reels = cachedReels
      ? cachedReels.slice(0, Settings.REEL_COUNT)
      : null;

    // Callback ref to track when mask is ready
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
        // Share mask with other components via context
        if (reelSetMaskRef) reelSetMaskRef.current = node;
        if (node && !maskReady) {
          setMaskReady(true);
        }
      },
      [maskReady, reelSetMaskRef],
    );

    const triggerLandingRef = useRef(triggerReelLandingAnimations);
    useEffect(() => {
      triggerLandingRef.current = triggerReelLandingAnimations;
    }, [triggerReelLandingAnimations]);

    // Batch landing animations - collect all reel data and trigger once when last reel stops
    // ONLY during turbo mode for optimal performance
    const pendingLandingAnimations = useRef(new Map()); // Map<reelIndex, reelData>
    const rafIdRef = useRef(null);

    const onReelStop = useCallback(
      (reelIndex, reelData) => {
        if (!reelData) return;

        // Check if turbo mode is active (spacebar turbo or autoplay)
        const isTurboMode = spacebarTurboActive || autoPlayActive;

        // TURBO MODE: Batch all landing animations until last reel stops
        if (isTurboMode) {
          // Store this reel's data
          pendingLandingAnimations.current.set(reelIndex, reelData);

          // If this is the last reel (index 4), trigger all landing animations at once
          if (reelIndex === Settings.REEL_COUNT - 1) {
            // Cancel any pending RAF
            if (rafIdRef.current !== null) {
              cancelAnimationFrame(rafIdRef.current);
            }

            // Collect all texture hide operations from all reels
            const allTextureHideOps = [];

            try {
              // Process all reels in order - show all spine animations first
              for (let i = 0; i < Settings.REEL_COUNT; i++) {
                const data = pendingLandingAnimations.current.get(i);
                if (data) {
                  // skipRAF=true to prevent individual RAF calls
                  const hideOps = triggerLandingRef.current?.(
                    i,
                    data,
                    null,
                    true,
                  );
                  if (hideOps && hideOps.length > 0) {
                    allTextureHideOps.push(...hideOps);
                  }
                }
              }
            } catch (err) {
              // Fail silently
            }

            // Single RAF to hide all textures at once (after all spine animations are set up)
            if (allTextureHideOps.length > 0) {
              rafIdRef.current = requestAnimationFrame(() => {
                allTextureHideOps.forEach(({ reelController, symbolIndex }) => {
                  if (reelController.hideSymbolTexture) {
                    reelController.hideSymbolTexture(symbolIndex);
                  }
                });

                // Clear pending animations
                pendingLandingAnimations.current.clear();
                rafIdRef.current = null;
              });
            } else {
              // No textures to hide, just clear
              pendingLandingAnimations.current.clear();
            }
          }
        } else {
          // REGULAR/MANUAL MODE: Trigger landing animations immediately per reel
          // This provides instant visual feedback for manual stops
          try {
            triggerLandingRef.current?.(reelIndex, reelData);
          } catch (err) {
            // Fail silently
          }
        }
      },
      [spacebarTurboActive, autoPlayActive],
    );

    // ✅ Memoized selector (no unnecessary re-renders)

    // Initialize and consume spine-extracted textures + generated fonts
    const { symbolsSpriteSheet, symbolsSpriteSheetBlurred } = useMemo(() => {
      if (app?.renderer) {
        SpineTextureExtractor.initialize(app.renderer);
        SpineAnimationData.initialize();
        FontGenerator.initialize(app.renderer);
      }
      return {
        symbolsSpriteSheet: SpineTextureExtractor.getSymbols(),
        symbolsSpriteSheetBlurred: SpineTextureExtractor.getBlurredSymbols(),
      };
    }, [app?.renderer]);

    // Cache symbols data
    const reelSymbolsData = useMemo(() => {
      if (!symbolsSpriteSheet) return null;
      return (
        symbolsSpriteSheet.data?.frames || symbolsSpriteSheet.textures || null
      );
    }, [symbolsSpriteSheet]);

    // Memoize reel container Y position (constant for all reels)
    const reelContainerY = useMemo(
      () =>
        -symbolHeight * (1.5 * Settings.SYMBOLS_VISIBLE - 0.5) - symbolHeight,
      [symbolHeight],
    );

    // Memoize reel container X positions (one per reel)
    const reelPositions = useMemo(() => {
      const centerOffset = (Settings.REEL_COUNT - 1) / 2;
      return reels
        ? reels.map((_, index) => ({
            x: reelSpacing * (index - centerOffset),
            y: reelContainerY,
          }))
        : [];
    }, [reels, reelSpacing, reelContainerY]);

    // Guard: Don't render until reels and sprite textures are ready
    // Blurred textures are optional (controlled by SPIN.BLURRED_TEXTURES_ENABLED)
    if (
      !reels ||
      !Array.isArray(reels) ||
      reels.length === 0 ||
      !symbolsSpriteSheet?.textures
    ) {
      return null;
    }

    return (
      <>
        <ReelSetMask
          ref={setMaskRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
        />

        {maskReady && (
          <Container
            visible={!holdAndWinActive && !freeSpinActive}
            name="reelset"
            mask={maskRef.current}
            x={0}
            y={0}
            zIndex={1}
          >
            {reels.map((reel, index) => {
              const position = reelPositions[index];
              if (!position) return null;

              return (
                <Container
                  key={index}
                  name={`reel_container_${index}`}
                  x={position.x}
                  y={position.y}
                >
                  <Reel
                    ref={(el) => (reelRefs.current[index] = el)}
                    reel={reel}
                    index={index}
                    symbolWidth={symbolWidth}
                    symbolHeight={symbolHeight}
                    symbolsSpriteSheet={symbolsSpriteSheet}
                    symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
                    reelSymbolsData={reelSymbolsData}
                    onReelStop={onReelStop}
                  />
                </Container>
              );
            })}
          </Container>
        )}
      </>
    );
  },
);

export default ReelSet;
